import React, { Suspense, useState, useEffect } from "react";
import {
  Redirect,
  Route,
  Switch,
  BrowserRouter as Router,
  withRouter,
} from "react-router-dom";

import config from "./config";
import Layout from "./components/Layout/";
import Loader from "./components/Shared/Loader";
import ScrollFixToTop from "./components/Shared/ScrollFixToTop";

import "./Apps.scss";
import "./assets/css/colors/default.css";
import "./assets/css/theme.css";




import "bootstrap/dist/css/bootstrap.min.css";


const TenantGroupIndex = React.lazy(() => import("./pages/TenantGroupIndex"));
const TenantIndex = React.lazy(() => import("./pages/TenantIndex"));
const Payment = React.lazy(() => import("./pages/Payment"));
const BookingConfirm = React.lazy(() => import("./pages/BookingConfirm"));
const Checkout = React.lazy(() => import("./pages/Checkout"));
const ThemePreview = React.lazy(() => import("./pages/ThemePreview"));

/** A site record as the pages read it: a hotel's, or a group's own. */
function toSiteData(ibeData) {
  return {
    bannerTitle1: ibeData.banner_title_1,
    bannerContent1: ibeData.banner_content_1,
    bannerImg1: ibeData.banner_img_url_1,
    bannerTitle2: ibeData.banner_title_2,
    bannerContent2: ibeData.banner_content_2,
    bannerImg2: ibeData.banner_img_url_2,
    bannerTitle3: ibeData.banner_title_3,
    bannerContent3: ibeData.banner_content_3,
    bannerImg3: ibeData.banner_img_url_3,
    fbLink: ibeData.fb_link,
    instaLink: ibeData.insta_link,
    logo: ibeData.logo_url,
    name: ibeData.name,
    id: ibeData.tenant_id,
    // The property's own details, for the footer and the confirmation.
    contact: ibeData.tenant ? {
      address: [ibeData.tenant.address_1, ibeData.tenant.address_2].map((s) => (s || '').trim()).filter(Boolean).join(', '),
      city: [ibeData.tenant.city, ibeData.tenant.region, ibeData.tenant.zip_code].filter(Boolean).join(', '),
      country: ibeData.tenant.e_country?.comment || '',
      phone: ibeData.tenant.phone_no || '',
      email: ibeData.tenant.email || '',
    } : null,
    stars: Number(ibeData.tenant?.star_rating) || 0,
  };
}

function App(props) {
  useEffect(() => {
    setIsLoading(true);
    getIbeFromWeburl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function getIbeFromWeburl() {
    // let namespace = "horizon-demo-group";
    let namespace = window.location.host.split(".")[0];

    // `namespace` comes off the hostname and used to be interpolated straight
    // into a Hasura query string. It is a path/query parameter now.
    const response = await fetch(
      `${config.backendUrl}/pms/ibe/site?namespace=${encodeURIComponent(namespace)}`
    );
    const ibeData = await response.json();
    if (ibeData == null) return;

    const site = toSiteData(ibeData);
    setTenantData(site);

    if (ibeData.is_tenant_group) {
      // A group's own site lists its member hotels, each linking to that
      // hotel's site. Membership comes from the hotels' group in the PMS.
      try {
        const res = await fetch(
          `${config.backendUrl}/pms/ibe/tenant-group/${encodeURIComponent(ibeData.tenant_group_id)}/sites`
        );
        setGroupHotels(res.ok ? await res.json() : []);
      } catch {
        setGroupHotels([]);
      }
      setIsTenantGroup(true);
    } else {
      setIsTenantGroup(false);
    }
    setIsLoading(false);
  }

  const [isLoading, setIsLoading] = useState(false);
  const [isTenantGroup, setIsTenantGroup] = useState(false);
  const [groupHotels, setGroupHotels] = useState([]);
  const [tenantData, setTenantData] = useState({
    bannerTitle1: "",
    bannerContent1: "",
    bannerImg1: "",
    bannerTitle2: "",
    bannerContent2: "",
    bannerImg2: "",
    bannerTitle3: "",
    bannerContent3: "",
    bannerImg3: "",
    fbLink: "",
    instaLink: "",
    logo: "",
    name: "",
    id: ""
  });

  useEffect(() => {
    if (tenantData.name) document.title = tenantData.name;
  }, [tenantData.name]);

  const loadingPlaceholder = (
    <div className="f-loader-wrap" style={{ minHeight: '70vh' }}>
      <div className="f-loader-orbit" aria-hidden />
    </div>
  );

  return (
    <div className="variant-f f-app">
      <Router>
        <ScrollFixToTop>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Route exact path="/" render={(props) => {
                  if (isLoading) {
                    return <Layout hasDarkTopBar={false} tenantData={tenantData}>{loadingPlaceholder}</Layout>;
                  }
                  return isTenantGroup
                    ? <Layout hasDarkTopBar={false} tenantData={tenantData}><TenantGroupIndex {...props} tenantData={tenantData} hotels={groupHotels} /></Layout>
                    : <Layout hasDarkTopBar={false} tenantData={tenantData}><TenantIndex {...props} tenantData={tenantData} /></Layout>;
                }} />
                {/* Rooms open in a panel over the results now; old links land there. */}
                <Route path="/room-type" render={() => <Redirect to="/" />} />
                <Route path="/checkout" render={(props) => <Layout hasDarkTopBar={false} tenantData={tenantData}><Checkout {...props} tenantData={tenantData} /></Layout>} />
                <Route path="/payment" render={(props) => <Layout hasDarkTopBar={false} tenantData={tenantData}><Payment {...props} tenantData={tenantData} /></Layout>} />
                <Route path="/booking-confirmation" render={(props) => <Layout hasDarkTopBar={false} tenantData={tenantData}><BookingConfirm {...props} tenantData={tenantData} /></Layout>} />
              <Route path="/theme-preview" render={() => <Layout hasDarkTopBar={false} tenantData={tenantData}><ThemePreview /></Layout>} />
            </Switch>
          </Suspense>
        </ScrollFixToTop>
      </Router>
    </div>
  );
}

export default withRouter(App);